import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, BookOpen, Sun, Moon, UsersRound, FileDown, Route, X } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/contexts/AuthContext';
import { addPdfHeader, getPdfHeaderHeight } from '@/utils/pdfHelper';
import ChildAvatar from '@/components/ChildAvatar';

const Courses = () => {
  const { canEdit, isAdmin } = useAuth();
  const canEditActivities = canEdit('activities') || isAdmin();
  
  const [activities, setCourses] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  
  // Modal de turmas da atividade
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityTurmas, setActivityTurmas] = useState(null);
  const [turmasLoading, setTurmasLoading] = useState(false);
  
  // Modal de trilha
  const [trilhaModalOpen, setTrilhaModalOpen] = useState(false);
  const [trilhaData, setTrilhaData] = useState(null);
  const [trilhaLoading, setTrilhaLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'oficina',
    schedule: {}, // {dia: [turnos]}
    professional_id: '',
    max_students: 30,
    allow_repeat: false
  });

  const daysOfWeek = [
    { value: 'segunda', label: 'Segunda' },
    { value: 'terca', label: 'Terça' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'quinta', label: 'Quinta' },
    { value: 'sexta', label: 'Sexta' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesRes, profsRes] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/professionals`)
      ]);
      setCourses(activitiesRes.data);
      setProfessionals(profsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar se pelo menos um dia/turno foi selecionado
    const hasSchedule = Object.keys(formData.schedule).some(day => 
      formData.schedule[day] && formData.schedule[day].length > 0
    );
    
    if (!hasSchedule) {
      toast.error('Selecione pelo menos um dia e turno');
      return;
    }

    // Extrair days_of_week e shifts do schedule para compatibilidade
    const days_of_week = Object.keys(formData.schedule).filter(day => 
      formData.schedule[day] && formData.schedule[day].length > 0
    );
    const allShifts = new Set();
    Object.values(formData.schedule).forEach(shifts => {
      if (shifts) shifts.forEach(s => allShifts.add(s));
    });

    const dataToSend = {
      ...formData,
      days_of_week,
      shifts: [...allShifts]
    };

    try {
      if (editingCourse) {
        await axios.put(`${API}/activities/${editingCourse.id}`, dataToSend);
        toast.success('Atividade atualizada com sucesso!');
      } else {
        await axios.post(`${API}/activities`, dataToSend);
        toast.success('Atividade cadastrada com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (activity) => {
    setEditingCourse(activity);
    
    // Converter formato antigo para novo se necessário
    let schedule = activity.schedule || {};
    
    // Se não tem schedule mas tem days_of_week e shifts/shift
    if (Object.keys(schedule).length === 0 && activity.days_of_week) {
      const shifts = activity.shifts && activity.shifts.length > 0 
        ? activity.shifts 
        : (activity.shift ? [activity.shift] : ['Vespertino']);
      
      activity.days_of_week.forEach(day => {
        schedule[day] = [...shifts];
      });
    }
    
    setFormData({
      name: activity.name || '',
      description: activity.description || '',
      type: activity.type || 'oficina',
      schedule: schedule,
      professional_id: activity.professional_id || '',
      max_students: activity.max_students || 30,
      allow_repeat: activity.allow_repeat || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir esta atividade?')) {
      try {
        await axios.delete(`${API}/activities/${id}`);
        toast.success('Atividade removida com sucesso!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao remover atividade');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'oficina',
      schedule: {},
      professional_id: '',
      max_students: 30,
      allow_repeat: false
    });
    setEditingCourse(null);
  };

  const toggleDayShift = (day, shift) => {
    setFormData(prev => {
      const newSchedule = { ...prev.schedule };
      
      if (!newSchedule[day]) {
        newSchedule[day] = [];
      }
      
      if (newSchedule[day].includes(shift)) {
        newSchedule[day] = newSchedule[day].filter(s => s !== shift);
        // Remover o dia se não tiver mais turnos
        if (newSchedule[day].length === 0) {
          delete newSchedule[day];
        }
      } else {
        newSchedule[day] = [...newSchedule[day], shift];
      }
      
      return { ...prev, schedule: newSchedule };
    });
  };

  const isShiftSelected = (day, shift) => {
    return formData.schedule[day]?.includes(shift) || false;
  };

  const getProfessionalName = (profId) => {
    const prof = professionals.find(p => p.id === profId);
    return prof ? prof.name : 'N/A';
  };

  const getScheduleDisplay = (activity) => {
    const schedule = activity.schedule || {};
    
    // Se não tem schedule, usar formato antigo
    if (Object.keys(schedule).length === 0) {
      const shifts = activity.shifts && activity.shifts.length > 0 
        ? activity.shifts 
        : (activity.shift ? [activity.shift] : []);
      const days = activity.days_of_week || [];
      
      if (days.length === 0) return 'Não configurado';
      
      const shiftText = shifts.map(s => s === 'Matutino' ? 'M' : 'T').join('/');
      return days.map(d => {
        const dayLabel = daysOfWeek.find(dw => dw.value === d)?.label || d;
        return `${dayLabel} (${shiftText})`;
      }).join(', ');
    }
    
    // Novo formato
    return Object.entries(schedule)
      .filter(([_, shifts]) => shifts && shifts.length > 0)
      .map(([day, shifts]) => {
        const dayLabel = daysOfWeek.find(d => d.value === day)?.label || day;
        const shiftText = shifts.map(s => s === 'Matutino' ? 'M' : 'T').join('/');
        return `${dayLabel} (${shiftText})`;
      })
      .join(', ');
  };

  const fetchActivityTurmas = async (activity) => {
    setTurmasLoading(true);
    setSelectedActivity(activity);
    try {
      const response = await axios.get(`${API}/activities/${activity.id}/turmas`);
      setActivityTurmas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setTurmasLoading(false);
    }
  };

  const handleActivityClick = (activity) => {
    fetchActivityTurmas(activity);
  };

  // Função para buscar trilha de uma turma
  const fetchTrilha = async (turma) => {
    setTrilhaLoading(true);
    setTrilhaModalOpen(true);
    try {
      const response = await axios.get(
        `${API}/activities/${selectedActivity.id}/turmas/${turma.day}/${turma.shift}/${turma.slot}/trilha`
      );
      setTrilhaData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar trilha');
      setTrilhaModalOpen(false);
    } finally {
      setTrilhaLoading(false);
    }
  };

  // Função para exportar trilha em PDF
  const exportTrilhaPDF = () => {
    if (!trilhaData || !trilhaData.children?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let startY = addPdfHeader(doc);

    // Título
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    const title = `Trilha - ${trilhaData.activity_name}`;
    doc.text(title, pageWidth / 2, startY, { align: 'center' });

    // Subtítulo
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    const subtitle = `${trilhaData.day_label} - ${trilhaData.shift_label} - ${trilhaData.slot_label}`;
    doc.text(subtitle, pageWidth / 2, startY + 7, { align: 'center' });

    // Total
    doc.setFontSize(10);
    doc.text(`Total: ${trilhaData.total_children} criancas`, pageWidth / 2, startY + 13, { align: 'center' });

    // Função para remover acentos
    const removeAccents = (str) => {
      if (!str) return '';
      return String(str)
        .replace(/[ãâáàä]/gi, 'a')
        .replace(/[êéèë]/gi, 'e')
        .replace(/[íìïî]/gi, 'i')
        .replace(/[õôóòö]/gi, 'o')
        .replace(/[úùüû]/gi, 'u')
        .replace(/[ç]/gi, 'c')
        .replace(/[ñ]/gi, 'n');
    };

    // Descobrir número máximo de atividades para criar colunas
    const maxActivities = Math.max(...trilhaData.children.map(c => c.trilha?.length || 0), 3);
    
    // Cabeçalhos
    const headers = ['Crianca'];
    for (let i = 1; i <= maxActivities; i++) {
      headers.push(`${i}o Horario`);
    }

    // Dados
    const rows = trilhaData.children.map(child => {
      const row = [removeAccents(child.name)];
      for (let i = 0; i < maxActivities; i++) {
        const act = child.trilha?.[i];
        row.push(act ? removeAccents(act.activity_name) : '-');
      }
      return row;
    });

    // Gerar tabela
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: startY + 18,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [217, 119, 78], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [254, 243, 239] },
      columnStyles: {
        0: { cellWidth: 60 }
      }
    });

    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Gerado em ${new Date().toLocaleDateString('pt-BR')} - Pagina ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const fileName = `trilha_${removeAccents(trilhaData.activity_name)}_${trilhaData.day}_${trilhaData.shift}.pdf`;
    doc.save(fileName.replace(/\s+/g, '_').toLowerCase());
    toast.success('PDF exportado com sucesso!');
  };

  // Função para exportar lista de horários em PDF
  const exportTurmasPDF = () => {
    if (!activityTurmas || !activityTurmas.turmas) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = addPdfHeader(doc);

    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Lista de Horários', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Nome da atividade
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(activityTurmas.activity_name || 'Atividade', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    // Informações gerais
    doc.setFontSize(10);
    doc.text(`Total de matrículas: ${activityTurmas.total_enrollments} | Vagas por horário: ${activityTurmas.max_students}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Cabeçalho da lista
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, yPos, pageWidth - margin * 2, 8, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('Dia', margin + 3, yPos + 5.5);
    doc.text('Turno', margin + 30, yPos + 5.5);
    doc.text('Horário', margin + 55, yPos + 5.5);
    doc.text('Faixa Etária', margin + 85, yPos + 5.5);
    doc.text('Ocupação', margin + 130, yPos + 5.5);
    yPos += 10;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Lista de horários
    activityTurmas.turmas.forEach((turma, index) => {
      // Nova página se necessário
      if (yPos > pageHeight - 25) {
        doc.addPage();
        yPos = addPdfHeader(doc);
        
        // Repetir cabeçalho
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos, pageWidth - margin * 2, 8, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Dia', margin + 3, yPos + 5.5);
        doc.text('Turno', margin + 30, yPos + 5.5);
        doc.text('Horário', margin + 55, yPos + 5.5);
        doc.text('Faixa Etária', margin + 85, yPos + 5.5);
        doc.text('Ocupação', margin + 130, yPos + 5.5);
        yPos += 10;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
      }

      // Linha alternada
      if (index % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, yPos - 2, pageWidth - margin * 2, 8, 'F');
      }

      doc.setFontSize(9);
      doc.text(turma.day_label || '-', margin + 3, yPos + 3);
      doc.text(turma.shift_label || '-', margin + 30, yPos + 3);
      doc.text(turma.slot_label || '-', margin + 55, yPos + 3);
      doc.text(turma.age_range || 'N/A', margin + 85, yPos + 3);
      
      // Colorir ocupação
      if (turma.count === 0) {
        doc.setTextColor(150, 150, 150);
      } else if (turma.count >= turma.max_students) {
        doc.setTextColor(220, 53, 69);
      } else {
        doc.setTextColor(34, 139, 34);
      }
      doc.text(turma.occupancy || '-', margin + 130, yPos + 3);
      doc.setTextColor(0, 0, 0);

      yPos += 8;
    });

    // Rodapé
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    doc.save(`horarios_${(activityTurmas.activity_name || 'atividade').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF exportado com sucesso!');
  };

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Atividades e Oficinas</h1>
          <p className="text-slate-600 mt-1">{canEditActivities ? 'Gerencie atividades e oficinas' : 'Visualização de atividades'}</p>
        </div>
        {canEditActivities && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-activity-button" className="bg-[#D87A4E] hover:bg-[#c26a42]">
              <Plus className="h-4 w-4 mr-2" />
              Nova Atividade
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCourse ? 'Editar Atividade' : 'Nova Atividade'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome da Atividade *</Label>
                  <Input
                    id="name"
                    data-testid="activity-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ex: Capoeira, Música, Dança..."
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <select
                    id="type"
                    data-testid="activity-type-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="oficina">Oficina</option>
                    <option value="convivencia">Convivência</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </div>

              {/* Grade de Dias e Turnos */}
              <div>
                <Label>Dias e Turnos *</Label>
                <p className="text-xs text-slate-500 mb-3">Clique para selecionar em quais dias e turnos esta atividade é oferecida</p>
                
                <div className="border rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-3 bg-slate-100 text-sm font-medium">
                    <div className="p-2 border-r">Dia</div>
                    <div className="p-2 border-r text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Sun className="h-4 w-4 text-amber-500" />
                        Manhã
                      </div>
                    </div>
                    <div className="p-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Moon className="h-4 w-4 text-indigo-500" />
                        Tarde
                      </div>
                    </div>
                  </div>
                  
                  {/* Rows */}
                  {daysOfWeek.map((day, index) => (
                    <div 
                      key={day.value} 
                      className={`grid grid-cols-3 ${index < daysOfWeek.length - 1 ? 'border-b' : ''}`}
                    >
                      <div className="p-2 border-r font-medium text-sm bg-slate-50">
                        {day.label}
                      </div>
                      <div className="p-2 border-r flex justify-center">
                        <button
                          type="button"
                          onClick={() => toggleDayShift(day.value, 'Matutino')}
                          className={`w-10 h-10 rounded-full transition-all ${
                            isShiftSelected(day.value, 'Matutino')
                              ? 'bg-amber-500 text-white shadow-md'
                              : 'bg-slate-100 text-slate-400 hover:bg-amber-100'
                          }`}
                        >
                          <Sun className="h-5 w-5 mx-auto" />
                        </button>
                      </div>
                      <div className="p-2 flex justify-center">
                        <button
                          type="button"
                          onClick={() => toggleDayShift(day.value, 'Vespertino')}
                          className={`w-10 h-10 rounded-full transition-all ${
                            isShiftSelected(day.value, 'Vespertino')
                              ? 'bg-indigo-500 text-white shadow-md'
                              : 'bg-slate-100 text-slate-400 hover:bg-indigo-100'
                          }`}
                        >
                          <Moon className="h-5 w-5 mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Resumo */}
                {Object.keys(formData.schedule).length > 0 && (
                  <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                    <strong>Resumo:</strong> {getScheduleDisplay({ schedule: formData.schedule })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="professional_id">Profissional Responsável</Label>
                  <select
                    id="professional_id"
                    data-testid="activity-professional-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.professional_id}
                    onChange={(e) => setFormData({ ...formData, professional_id: e.target.value })}
                  >
                    <option value="">Nenhum</option>
                    {professionals.map(prof => (
                      <option key={prof.id} value={prof.id}>{prof.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="max_students">Máx. Alunos por Turma *</Label>
                  <Input
                    id="max_students"
                    type="number"
                    data-testid="activity-maxstudents-input"
                    value={formData.max_students}
                    onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="allow_repeat"
                  checked={formData.allow_repeat}
                  onChange={(e) => setFormData({ ...formData, allow_repeat: e.target.checked })}
                  className="w-4 h-4 text-[#D87A4E] rounded focus:ring-[#D87A4E]"
                />
                <Label htmlFor="allow_repeat" className="cursor-pointer text-sm">
                  Permitir repetição desta atividade na grade semanal da criança
                </Label>
              </div>
              
              <div>
                <Label htmlFor="description">Descrição</Label>
                <textarea
                  id="description"
                  data-testid="activity-description-textarea"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva a atividade..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-activity-button" className="bg-[#D87A4E] hover:bg-[#c26a42]">
                  {editingCourse ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D87A4E]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="activities-list">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="bg-white rounded-xl shadow-md p-6 border border-slate-100 card-hover cursor-pointer"
              onClick={() => handleActivityClick(activity)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-[#D87A4E]/20 rounded-full flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-[#D87A4E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{activity.name}</h3>
                    <p className="text-xs text-slate-500">{activity.type === 'oficina' ? 'Oficina' : activity.type === 'outro' ? 'Outro' : 'Convivência'}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Horários:</span>
                  <p className="font-medium text-slate-800 text-xs mt-1">
                    {getScheduleDisplay(activity)}
                  </p>
                </div>
                <div>
                  <span className="text-slate-600">Profissional:</span>
                  <p className="font-medium text-slate-800">{getProfessionalName(activity.professional_id)}</p>
                </div>
                <div>
                  <span className="text-slate-600">Máx. Alunos:</span>
                  <p className="font-medium text-slate-800">{activity.max_students}</p>
                </div>
              </div>
              {canEditActivities && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={(e) => { e.stopPropagation(); handleEdit(activity); }}
                  data-testid={`edit-activity-${activity.id}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); handleDelete(activity.id); }}
                  data-testid={`delete-activity-${activity.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && activities.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhuma atividade cadastrada</p>
        </div>
      )}

      {/* Modal de Turmas da Atividade */}
      <Dialog open={!!selectedActivity} onOpenChange={() => { setSelectedActivity(null); setActivityTurmas(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D87A4E]/20 rounded-full flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-[#D87A4E]" />
                </div>
                <div>
                  <span>{selectedActivity?.name}</span>
                  <p className="text-sm font-normal text-slate-500">
                    {selectedActivity?.type === 'oficina' ? 'Oficina' : selectedActivity?.type === 'outro' ? 'Outro' : 'Convivência'} • Máx. {selectedActivity?.max_students} alunos
                  </p>
                </div>
              </div>
              {activityTurmas && activityTurmas.turmas?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportTurmasPDF}
                  className="bg-red-50 hover:bg-red-100 border-red-200"
                >
                  <FileDown className="h-4 w-4 mr-2 text-red-600" />
                  Exportar PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {turmasLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D87A4E]"></div>
            </div>
          ) : activityTurmas ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="px-3 py-1 bg-slate-100 rounded-full">
                  {(() => {
                    const total = activityTurmas.turmas?.length || 0;
                    const filled = activityTurmas.turmas?.filter(t => t.count > 0).length || 0;
                    return `${filled}/${total} horário(s) preenchido(s)`;
                  })()}
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                  {activityTurmas.total_enrollments || 0} matrícula(s)
                </span>
              </div>
              
              {/* Formato: Dia - turno - horário - faixa etária - ocupação */}
              <div className="text-xs text-slate-500 mb-2 px-2">
                Formato: Dia - turno - horário - faixa etária - ocupação
              </div>
              
              {activityTurmas.turmas?.length > 0 ? (
                <div className="space-y-2">
                  {activityTurmas.turmas.map((turma, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border ${
                        turma.count === 0 
                          ? 'bg-slate-50 border-slate-200 opacity-70'
                          : turma.count >= (activityTurmas.max_students || 20) 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <UsersRound className={`h-4 w-4 flex-shrink-0 ${
                            turma.count === 0 
                              ? 'text-slate-300'
                              : turma.count >= (activityTurmas.max_students || 20) 
                                ? 'text-amber-600' 
                                : 'text-green-600'
                          }`} />
                          <p className="text-sm text-slate-800">
                            <span className="font-semibold">{turma.day_label}</span>
                            <span className="text-slate-400 mx-1">-</span>
                            <span>{turma.shift_label}</span>
                            <span className="text-slate-400 mx-1">-</span>
                            <span>{turma.slot_label}</span>
                            <span className="text-slate-400 mx-1">-</span>
                            <span className={turma.age_range === 'N/A' ? 'text-slate-400 italic' : ''}>
                              {turma.age_range}
                            </span>
                            <span className="text-slate-400 mx-1">-</span>
                            <span className={`font-medium ${
                              turma.count === 0 
                                ? 'text-slate-400'
                                : turma.count >= (activityTurmas.max_students || 20)
                                  ? 'text-amber-700'
                                  : 'text-green-700'
                            }`}>
                              {turma.occupancy}
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Lista de crianças (expansível) */}
                      {turma.children && turma.children.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                            Ver crianças matriculadas ({turma.count})
                          </summary>
                          <div className="mt-2 pl-6 text-xs text-slate-600 space-y-1">
                            {turma.children.map((child, idx) => (
                              <div key={idx}>
                                {child.name} ({child.age} anos)
                              </div>
                            ))}
                            
                            {/* Botões de ação */}
                            <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchTrilha(turma)}
                                className="text-xs"
                              >
                                <Route className="h-3 w-3 mr-1" />
                                Ver Trilha
                              </Button>
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Nenhum horário configurado para esta atividade
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Modal de Trilha */}
      <Dialog open={trilhaModalOpen} onOpenChange={setTrilhaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Route className="h-5 w-5 text-orange-600" />
              Trilha de Atividades
            </DialogTitle>
          </DialogHeader>

          {trilhaLoading ? (
            <div className="py-8 text-center text-slate-500">Carregando trilha...</div>
          ) : trilhaData ? (
            <div className="space-y-4">
              {/* Cabeçalho do relatório */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
                <h3 className="font-bold text-lg text-slate-800">{trilhaData.activity_name}</h3>
                <p className="text-slate-600">
                  {trilhaData.day_label} - {trilhaData.shift_label} - {trilhaData.slot_label}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {trilhaData.total_children} crianças matriculadas
                </p>
              </div>

              {/* Botão de exportar PDF */}
              <div className="flex justify-end">
                <Button onClick={exportTrilhaPDF} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                  <FileDown className="h-4 w-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>

              {/* Lista de crianças com trilha */}
              <div className="space-y-2">
                {trilhaData.children?.map((child, index) => (
                  <div 
                    key={child.id || index}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:shadow-sm transition-shadow"
                  >
                    <ChildAvatar photo={child.photo_url} name={child.name} size="sm" zoomable={true} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{child.name}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 flex-wrap">
                        {child.trilha?.map((act, actIndex) => (
                          <span key={actIndex} className="flex items-center">
                            <span className={`px-2 py-0.5 rounded-full ${
                              act.activity_name === trilhaData.activity_name
                                ? 'bg-orange-100 text-orange-700 font-medium'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {act.activity_name}
                            </span>
                            {actIndex < child.trilha.length - 1 && (
                              <span className="mx-1 text-slate-400">→</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Courses;
